import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, SectionCard, StatusBadge, TierBadge, Empty } from "@/components/shared";
import { fmtDate } from "@/lib/format";
import { Plus, Search, X } from "lucide-react";
import { toast } from "sonner";

export default function Partners() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ types: [], cities: [] });
  const [q, setQ] = useState("");
  const [ftype, setFtype] = useState("");
  const [fstatus, setFstatus] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (ftype) params.partner_type = ftype;
    if (fstatus) params.status = fstatus;
    api.get("/partners", { params }).then((r) => setRows(r.data));
  };

  useEffect(() => { api.get("/partners/meta").then((r) => setMeta(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, ftype, fstatus]);

  return (
    <div>
      <PageHeader title="Partners" subtitle="HVAC channel partners with unique IDs, KYC and tier status."
        actions={<button data-testid="add-partner-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 text-white font-semibold px-4 py-2 rounded-lg copper-glow" style={{ background: "var(--copper)" }}><Plus size={16} /> Onboard Partner</button>} />

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="partner-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, ID, mobile, company…"
            className="w-full bg-white border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm" />
        </div>
        <select data-testid="filter-type" value={ftype} onChange={(e) => setFtype(e.target.value)} className="bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm">
          <option value="">All Types</option>
          {meta.types.map((t) => <option key={t}>{t}</option>)}
        </select>
        <select data-testid="filter-status" value={fstatus} onChange={(e) => setFstatus(e.target.value)} className="bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm">
          <option value="">All Status</option>
          <option value="verified">Verified</option>
          <option value="pending_verification">Pending</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <SectionCard className="!p-0" testid="partners-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="px-5 py-3">Partner ID</th><th className="px-5 py-3">Name</th><th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">City</th><th className="px-5 py-3">Tier</th><th className="px-5 py-3">Points</th>
                <th className="px-5 py-3">Status</th><th className="px-5 py-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} data-testid={`partner-row-${p.partner_id}`} onClick={() => nav(`/partners/${p.id}`)}
                  className="border-b border-slate-50 hover:bg-orange-50/30 cursor-pointer">
                  <td className="px-5 py-3 font-mono text-xs font-semibold text-slate-700">{p.partner_id}</td>
                  <td className="px-5 py-3"><p className="font-medium text-slate-800">{p.name}</p><p className="text-xs text-slate-400">{p.company}</p></td>
                  <td className="px-5 py-3 text-slate-600">{p.partner_type}</td>
                  <td className="px-5 py-3 text-slate-600">{p.city}</td>
                  <td className="px-5 py-3"><TierBadge tier={p.tier} /></td>
                  <td className="px-5 py-3 font-mono text-slate-700">{p.points_balance?.toLocaleString("en-IN")}</td>
                  <td className="px-5 py-3"><StatusBadge status={p.status} /></td>
                  <td className="px-5 py-3 text-xs text-slate-500">{fmtDate(p.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <Empty text="No partners match your filters." />}
        </div>
      </SectionCard>

      {open && <OnboardWizard meta={meta} onClose={() => setOpen(false)} onDone={(p) => { setOpen(false); toast.success(`Partner created: ${p.partner_id}`); load(); nav(`/partners/${p.id}`); }} />}
    </div>
  );
}

function OnboardWizard({ meta, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ name: "", partner_type: "Consultant", mobile: "", email: "", city: "Mumbai", company: "", gst: "", pan: "", address: "", specialization: "", consent: true });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/partners", f);
      onDone(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const canNext = step === 1 ? f.name && f.mobile && f.partner_type && f.city : true;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="partner-onboarding-wizard">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div><h3 className="font-bold text-slate-900">Onboard Partner</h3><p className="text-xs text-slate-400">Step {step} of 3</p></div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {step === 1 && <>
            <Field label="Full Name *"><input data-testid="onb-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="inp" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Partner Type *"><select data-testid="onb-type" value={f.partner_type} onChange={(e) => set("partner_type", e.target.value)} className="inp">{meta.types.map((t) => <option key={t}>{t}</option>)}</select></Field>
              <Field label="City *"><select data-testid="onb-city" value={f.city} onChange={(e) => set("city", e.target.value)} className="inp">{meta.cities.map((c) => <option key={c}>{c}</option>)}</select></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Mobile *"><input data-testid="onb-mobile" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} className="inp" /></Field>
              <Field label="Email"><input data-testid="onb-email" value={f.email} onChange={(e) => set("email", e.target.value)} className="inp" /></Field>
            </div>
          </>}
          {step === 2 && <>
            <Field label="Company"><input value={f.company} onChange={(e) => set("company", e.target.value)} className="inp" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="GST"><input value={f.gst} onChange={(e) => set("gst", e.target.value)} className="inp" /></Field>
              <Field label="PAN"><input value={f.pan} onChange={(e) => set("pan", e.target.value)} className="inp" /></Field>
            </div>
            <Field label="Specialization"><input value={f.specialization} onChange={(e) => set("specialization", e.target.value)} className="inp" placeholder="VRF Systems, Chillers…" /></Field>
            <Field label="Address"><textarea value={f.address} onChange={(e) => set("address", e.target.value)} className="inp" rows={2} /></Field>
          </>}
          {step === 3 && <>
            <div className="bg-slate-50 rounded-lg p-4 text-sm space-y-1">
              <p><b>{f.name}</b> · {f.partner_type}</p>
              <p className="text-slate-500">{f.mobile} · {f.city} · {f.company || "—"}</p>
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-600">
              <input type="checkbox" data-testid="onb-consent" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5" />
              I confirm consent to store & process this partner's data (v1.0). A unique Partner ID and digital card will be generated on submit.
            </label>
          </>}
        </div>
        <div className="flex justify-between px-6 py-4 border-t border-slate-100">
          <button onClick={() => (step === 1 ? onClose() : setStep(step - 1))} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">{step === 1 ? "Cancel" : "Back"}</button>
          {step < 3 ? (
            <button data-testid="onb-next" disabled={!canNext} onClick={() => setStep(step + 1)} className="px-5 py-2 text-sm text-white font-semibold rounded-lg disabled:opacity-50" style={{ background: "var(--navy-800)" }}>Next</button>
          ) : (
            <button data-testid="onb-submit" disabled={busy || !f.consent} onClick={submit} className="px-5 py-2 text-sm text-white font-semibold rounded-lg copper-glow disabled:opacity-50" style={{ background: "var(--copper)" }}>{busy ? "Creating…" : "Generate Partner ID"}</button>
          )}
        </div>
      </div>
      <style>{`.inp{width:100%;border:1px solid #CBD5E1;border-radius:8px;padding:8px 10px;font-size:14px;background:#fff}.inp:focus{outline:none;border-color:var(--copper)}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-xs font-semibold text-slate-600 block mb-1">{label}</label>{children}</div>;
}
